import React, { useState, useRef, useEffect } from "react";
import {
  VisualizationData,
  ChartDataItem,
  PIE_COLORS,
  buildChartData,
  buildChartTitle,
  getGraphTypeOptions,
} from "./graphUtils";
import GraphRenderer from "./GraphRenderer";

interface GraphPopupModalProps {
  isOpen: boolean;
  onClose: () => void;
  visualizationData: VisualizationData | null;
  title?: string;
  teamColor?: string;
}

// ── Dropdown Button ───────────────────────────────────────────────────────────
function DropdownButton({ label, options, selected, onSelect, teamColor }: {
  label: string;
  options: { value: string; label: string }[];
  selected: string;
  onSelect: (v: string) => void;
  teamColor: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "#fff", border: `1.5px solid ${teamColor}`,
          borderRadius: 6, padding: "6px 14px", fontSize: 13,
          fontWeight: 600, color: teamColor, cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        {label}<span style={{ fontSize: 9 }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 200, minWidth: 140, overflow: "hidden",
        }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onSelect(opt.value); setOpen(false); }}
              style={{
                padding: "10px 16px", fontSize: 13,
                fontWeight: selected === opt.value ? 700 : 500,
                color: selected === opt.value ? teamColor : "#333",
                background: selected === opt.value ? `${teamColor}12` : "#fff",
                borderLeft: selected === opt.value ? `3px solid ${teamColor}` : "3px solid transparent",
                cursor: "pointer",
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pie Legend ────────────────────────────────────────────────────────────────
const ModernPieLegend = ({ data, total, activeIndex, setActiveIndex }: {
  data: ChartDataItem[];
  total: number;
  activeIndex: number | null;
  setActiveIndex: (i: number | null) => void;
}) => (
  <div style={{
    display: "flex", flexDirection: "column", gap: 6,
    maxHeight: 320, overflowY: "auto", paddingRight: 4,
  }}>
    {data.map((item, i) => {
      const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
      const isActive = activeIndex === i;
      const color = PIE_COLORS[i % PIE_COLORS.length];
      return (
        <div
          key={i}
          onMouseEnter={() => setActiveIndex(i)}
          onMouseLeave={() => setActiveIndex(null)}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "7px 10px", borderRadius: 8,
            background: isActive ? `${color}18` : "transparent",
            border: isActive ? `1px solid ${color}55` : "1px solid transparent",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.34,1.56,0.64,1)",
            transform: isActive ? "translateX(4px)" : "translateX(0)",
          }}
        >
          <div style={{
            width: isActive ? 12 : 10,
            height: isActive ? 12 : 10,
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
            boxShadow: isActive ? `0 0 10px ${color}, 0 0 20px ${color}55` : "none",
            transition: "all 0.2s ease",
          }} />
          <span style={{
            fontSize: 12,
            color: isActive ? color : "#555",
            fontWeight: isActive ? 700 : 500,
            flex: 1, whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis", maxWidth: 160,
            transition: "color 0.2s ease",
          }}>
            {isActive ? `${item.name} — ${item.value.toLocaleString()}` : item.name}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 50, height: 4, borderRadius: 4, background: "#eee", overflow: "hidden" }}>
              <div style={{
                width: `${pct}%`, height: "100%", background: color,
                borderRadius: 4,
                transition: "width 0.5s cubic-bezier(0.34,1.56,0.64,1)",
              }} />
            </div>
            <span style={{
              fontSize: 11, color, fontWeight: 700,
              minWidth: 36, textAlign: "right",
              transition: "transform 0.2s ease",
              transform: isActive ? "scale(1.1)" : "scale(1)",
              display: "inline-block",
            }}>
              {pct}%
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

// ── VisualizationSpinner ──────────────────────────────────────────────────────
export function VisualizationSpinner({ teamColor }: { teamColor: string }) {
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(3px)",
      zIndex: 9999,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: 20,
      animation: "gp-fadeIn 0.2s ease",
    }}>
      <div style={{ position: "relative", width: 72, height: 72 }}>
        <div style={{
          position: "absolute", inset: 0,
          border: `3px solid ${teamColor}22`,
          borderRadius: "50%",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          border: `3px solid transparent`,
          borderTopColor: teamColor,
          borderRightColor: `${teamColor}88`,
          borderRadius: "50%",
          animation: "gp-spin 0.8s linear infinite",
        }} />
        <div style={{
          position: "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 16, height: 16,
          borderRadius: "50%",
          background: teamColor,
          animation: "gp-pulse 0.8s ease-in-out infinite",
        }} />
      </div>
      <p style={{
        color: "#fff", fontSize: 14, fontWeight: 600,
        letterSpacing: 0.5, margin: 0,
        animation: "gp-fadeInUp 0.4s ease 0.1s both",
      }}>
        Building your visualization…
      </p>
      <style>{`
        @keyframes gp-spin { to { transform: rotate(360deg) } }
        @keyframes gp-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 }
          50% { transform: translate(-50%, -50%) scale(0.6); opacity: 0.5 }
        }
        @keyframes gp-fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes gp-fadeInUp {
          from { opacity: 0; transform: translateY(8px) }
          to { opacity: 1; transform: translateY(0) }
        }
      `}</style>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function GraphPopupModal({
  isOpen, onClose, visualizationData, title = "", teamColor = "#A8042D",
}: GraphPopupModalProps) {
  const [chartType, setChartType] = useState<"bar" | "pie">("bar");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [chartReady, setChartReady] = useState(false);
  const [playerLimit, setPlayerLimit] = useState<number>(10);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setChartReady(true), 50);
      return () => clearTimeout(timer);
    } else {
      setChartReady(false);
    }
  }, [isOpen]);

  if (!isOpen || !visualizationData) return null;

  // ── Error state — show message instead of chart ──
  if (visualizationData?.error) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(3px)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "gp-fadeIn 0.2s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14,
              width: "min(480px, 92vw)", padding: "36px 32px",
              boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
              animation: "gp-slideUp 0.26s cubic-bezier(0.34,1.56,0.64,1)",
              display: "flex", flexDirection: "column",
              alignItems: "center", gap: 16, textAlign: "center",
            }}
          >
            <span style={{ fontSize: 48 }}>📊</span>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#333" }}>
              Visualization Unavailable
            </p>
            <p style={{ margin: 0, fontSize: 14, color: "#666", lineHeight: 1.6 }}>
              {visualizationData.message}
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop: 8, padding: "10px 28px",
                background: teamColor, color: "#fff",
                border: "none", borderRadius: 8,
                fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
        <style>{`
          @keyframes gp-fadeIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes gp-slideUp {
            from { opacity: 0; transform: translateY(24px) scale(0.97) }
            to   { opacity: 1; transform: translateY(0) scale(1) }
          }
        `}</style>
      </>
    );
  }

  // ── Detect query type ─────────────────────────────────────────────────────
  // BUG FIX 1: Guard simple/basic from ever being treated as complex
  const isComplex =
    visualizationData.query_type !== "simple" &&
    visualizationData.query_type !== "basic" &&
    visualizationData.stat_columns &&
    Array.isArray(visualizationData.stat_columns) &&
    visualizationData.stat_columns.length >= 2;

  const isMin    = visualizationData.query_type === "min";
  const isMax    = visualizationData.query_type === "max";
  const isStreak = visualizationData.query_type === "streak";
  const isLtw    = visualizationData.query_type === "ltw";
  const isTrend  = visualizationData.query_type === "trend";
  const isAvg    = visualizationData.query_type === "avg";
  const isSum    = visualizationData.query_type === "sum";
  const isRank   = visualizationData.query_type === "rank";
  const isCount  = visualizationData.query_type === "count";

  // Hide player limit + graph type dropdowns for windowed chart types
  const isWindowed = isMin || isMax || isStreak || isLtw || isTrend || isAvg || isSum || isRank || isCount;

  // Extract stat label from sql_query
  const statLabel: string = (() => {
    const matches = (visualizationData.sql_query ?? "").match(/\bs_([a-z0-9_]+)/g);
    if (!matches) return "Stat Value";
    const statMatch = matches.find(m => !m.includes("interim") && !m.includes("pgs"));
    if (!statMatch) return "Stat Value";
    return statMatch
      .replace("s_", "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  })();

  // ── Chart data (only used for simple/basic/pie) ───────────────────────────
  const chartData      = buildChartData(visualizationData, playerLimit);
  const hasData        = chartData.length > 0;
  const total          = chartData.reduce((sum, d) => sum + d.value, 0);
  const chartTitle     = buildChartTitle(visualizationData.query_type, title);
  const graphTypeOptions = getGraphTypeOptions();

  const refreshChart = () => {
    setChartReady(false);
    setTimeout(() => setChartReady(true), 50);
  };

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(3px)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "gp-fadeIn 0.2s ease",
        }}
      >
        {/* ── Modal card ── */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#eef0f4", borderRadius: 14,
            width: "min(900px, 96vw)", maxHeight: "92vh", overflowY: "auto",
            padding: "20px 24px 28px",
            boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
            animation: "gp-slideUp 0.26s cubic-bezier(0.34,1.56,0.64,1)",
            position: "relative",
          }}
        >
          {/* ── Top bar ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>

            {/* Players limit — hidden for complex & windowed types */}
            {!isComplex && !isWindowed && (
              <DropdownButton
                label={`Players: ${playerLimit}`}
                teamColor={teamColor}
                selected={String(playerLimit)}
                options={[
                  { value: "10", label: "10 Players" },
                  { value: "15", label: "15 Players" },
                  { value: "20", label: "20 Players" },
                ]}
                onSelect={(v) => {
                  setPlayerLimit(Number(v));
                  setActiveIndex(null);
                  refreshChart();
                }}
              />
            )}

            {/* Graph type — hidden for complex & windowed types */}
            {!isComplex && !isWindowed && (
              <DropdownButton
                label="Graph Types"
                teamColor={teamColor}
                selected={chartType}
                options={graphTypeOptions}
                onSelect={(v) => {
                  setChartType(v as "bar" | "pie");
                  setActiveIndex(null);
                  refreshChart();
                }}
              />
            )}

            <button
              onClick={onClose}
              style={{
                marginLeft: "auto", background: "transparent", border: "none",
                fontSize: 20, color: "#555", cursor: "pointer", lineHeight: 1, padding: "0 4px",
              }}
            >
              ✕
            </button>
          </div>

          {/* ── White card ── */}
          <div style={{
            background: "#fff", borderRadius: 12,
            padding: "28px 20px 20px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}>
            <p style={{
              textAlign: "center", fontSize: 13, fontWeight: 700, color: "#333",
              letterSpacing: 0.5, margin: "0 0 20px 0", textTransform: "uppercase",
            }}>
              {chartTitle}
            </p>

            {/* ── Chart area ──
                BUG FIX 2: Special types checked first, empty state only
                applies to simple/basic at the end — never blocks them. ── */}
            {isComplex ? (
              <GraphRenderer
                queryType="complex"
                chartData={visualizationData as any}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={visualizationData.total_records || 0}
                complexData={visualizationData as any}
              />

            ) : isMin ? (
              <GraphRenderer
                queryType="min"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                minData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isMax ? (
              <GraphRenderer
                queryType="max"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                maxData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isStreak ? (
              <GraphRenderer
                queryType="streak"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                streakData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isLtw ? (
              <GraphRenderer
                queryType="ltw"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                ltwData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isTrend ? (
              <GraphRenderer
                queryType="trend"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                trendData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isAvg ? (
              <GraphRenderer
                queryType="avg"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                avgData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isSum ? (
              <GraphRenderer
                queryType="sum"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                sumData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isRank ? (
              <GraphRenderer
                queryType="rank"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                rankData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : isCount ? (
              <GraphRenderer
                queryType="count"
                chartData={[]}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={0}
                countData={visualizationData as any}
                statLabel={statLabel}
              />

            ) : !hasData ? (
              // Empty state — only reached for simple/basic with no data
              <div style={{
                height: 300, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", color: "#bbb", gap: 8,
              }}>
                <span style={{ fontSize: 36 }}>📭</span>
                <p style={{ margin: 0, fontSize: 14 }}>No visualization data available</p>
              </div>

            ) : chartType === "bar" ? (
              <GraphRenderer
                queryType={visualizationData.query_type}
                chartData={chartData}
                chartType="bar"
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                chartReady={chartReady}
                teamColor={teamColor}
                total={total}
              />

            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 24, minHeight: 360 }}>
                <GraphRenderer
                  queryType={visualizationData.query_type}
                  chartData={chartData}
                  chartType="pie"
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  chartReady={chartReady}
                  teamColor={teamColor}
                  total={total}
                />
                <div style={{
                  flex: 1, minWidth: 0,
                  animation: "gp-legendSlide 0.5s ease 0.3s both",
                }}>
                  <p style={{
                    margin: "0 0 10px 0", fontSize: 11, fontWeight: 700,
                    color: "#aaa", letterSpacing: 0.8, textTransform: "uppercase",
                  }}>
                    Breakdown
                  </p>
                  <ModernPieLegend
                    data={chartData}
                    total={total}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                  />
                </div>
              </div>
            )}

          </div>{/* end white card */}
        </div>{/* end modal card */}
      </div>{/* end backdrop */}

      <style>{`
        @keyframes gp-fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes gp-slideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.97) }
          to   { opacity: 1; transform: translateY(0) scale(1) }
        }
        @keyframes gp-donutEntrance {
          from { opacity: 0; transform: scale(0.7) rotate(-15deg) }
          to   { opacity: 1; transform: scale(1) rotate(0deg) }
        }
        @keyframes gp-legendSlide {
          from { opacity: 0; transform: translateX(20px) }
          to   { opacity: 1; transform: translateX(0) }
        }
        .apexcharts-tooltip {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
        }
      `}</style>
    </>
  );
}